import { PlusIcon } from '@heroicons/react/24/solid'
import { withZod } from '@remix-validated-form/with-zod'
import { ReactElement, type ReactNode, useState } from 'react'
import { z } from 'zod'
import { FormColorSelector } from '../form/FormColorSelector'
import Button from 'app/components/Button'
import FormInput from 'app/components/form/FormInput'
import { SmartForm } from 'app/components/form/SmartForm'
import type { PipelineType } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { getPipelineColors } from 'lib/getPipelineColors'
import { debug } from 'lib/logging'

const newListSchema = withZod(
  z.object({
    name: z
      .string()
      .min(1, { message: `Name is required` })
      .max(255, { message: `Name must be less than 255 characters` })
      .transform((value) => value.replace(/\//g, ` `)),
  }),
)

export function AddPipeline({
  pipeline,
  createPipeline,
  children,
}: {
  pipeline?: PipelineType
  createPipeline: (data: any) => Promise<void>
  /** More fields, drawn under the stage, such as who can see a new list. */
  children?: ReactNode
}): ReactElement {
  const [addStage, setAddStage] = useState<boolean>(false)
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)

  const handleSubmit = async () => {
    setLoading(true)
    let form = document.getElementById(`new-pipeline`) as HTMLFormElement
    let data = Object.fromEntries(new FormData(form).entries())
    let result = await newListSchema.validate(data)
    if (result.error) {
      debug(`customPages`, `error`, result.error)
      if (result?.error?.fieldErrors?.name) {
        setNameError(result?.error?.fieldErrors?.name)
      }
      setLoading(false)
      return
    } else if (nameError) {
      setNameError(undefined)
    }
    await createPipeline(data)
    closeModal()
  }
  return (
    <>
      <SmartForm
        id="new-pipeline"
        className="mt-0! min-h-min w-170 gap-y-0! px-6"
        validator={newListSchema}
        preventSubmit
        defaultValues={{ name: pipeline?.name }}
        hideButtons
      >
        <FormInput
          name="name"
          label="Name"
          labelClassName="mb-1.5 text-sm font-medium leading-5 text-strong"
          placeholder="Eg.: List 1"
          className="rounded-lg border border-default bg-surface p-2! text-body-md shadow-elevation-1 placeholder:text-subtle"
          wrapperClassName={nameError ? `pt-4 pb-0` : `py-4`}
          errorClassName="!mt-0.5 pb-4"
          error={nameError}
          cols={6}
        />
        <div className="col-span-6">
          <span className="mb-1.5 flex flex-col text-sm font-medium leading-5 text-strong">Stage</span>
          <div
            className="col-span-6 cursor-pointer rounded-lg border border-default bg-surface px-2 py-2 shadow-elevation-1"
            onClick={() => setAddStage(true)}
          >
            {addStage ? (
              <>
                <FormInput name="stage" placeholder="Eg: Stage 1" className="p-2!" cols={6} />
                <FormColorSelector
                  name="color"
                  defaultColor="pipelines-silver"
                  presetColors={getPipelineColors()}
                  colorClassName="h-8 w-8 grow cursor-pointer flex space-x-2"
                  wrapperClassName="flex items-center mt-4"
                  cols={6}
                />
              </>
            ) : (
              <button className="flex items-center space-x-2 p-2 text-ui-xs" type="button">
                <PlusIcon width={16} height={16} />
                <p className="text-strong">Add stage</p>
              </button>
            )}
          </div>
        </div>
        {children && <div className="col-span-6 pt-4">{children}</div>}
        <div className="col-span-6 mt-4 flex justify-end gap-x-2 border-t border-default px-6 py-3">
          <Button label="Cancel" mode="white" disabled={loading} onClick={closeModal} />
          <Button
            label="Save"
            mode="primary"
            form="new-pipeline"
            type="submit"
            onClick={handleSubmit}
            loading={loading}
          />
        </div>
      </SmartForm>
    </>
  )
}
